import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiArrowSquareOutLight, PiDownloadSimpleLight, PiSealCheckLight } from "react-icons/pi";
import client from "../../api/client";

const BACKEND = process.env.REACT_APP_DOMAIN_URL || "";

function CertificateCard({ cert }) {
  const [downloading, setDownloading] = useState(false);

  const viewUrl     = `${BACKEND}/certificate/${cert.uuid}/view`;
  const downloadUrl = `${BACKEND}/api/certificates/${cert.uuid}/download`;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(downloadUrl, {
        headers: { Authorization: client.defaults.headers.Authorization || "" },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `Certificate_${cert.certificate_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download certificate. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const issueDate = cert.issue_date
    ? new Date(cert.issue_date).toLocaleDateString("en-US", {
        year: "numeric", month: "long", day: "numeric",
      })
    : "—";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm
      hover:shadow-md transition-shadow duration-200 overflow-hidden">

      {/* Orange top strip */}
      <div className="h-1.5 bg-gradient-to-r from-[#F05A19] to-[#ff8c5a]" />

      <div className="p-5">
        {/* Approved badge */}
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700
            text-xs font-semibold px-2.5 py-1 rounded-full border border-green-200">
            <PiSealCheckLight size={14} />
            Approved
          </span>
          <span className="text-xs text-gray-400 font-mono">{cert.certificate_number}</span>
        </div>

        {/* Course name */}
        <h3 className="text-base font-semibold text-gray-800 leading-snug mb-1 line-clamp-2">
          {cert.course?.title || "—"}
        </h3>

        {/* Cohort + date */}
        <div className="flex flex-col gap-0.5 mb-4">
          {cert.cohort?.name && (
            <p className="text-sm text-gray-500">{cert.cohort.name}</p>
          )}
          <p className="text-sm text-gray-400">Issued: {issueDate}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <a
            href={viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5
              border border-[#F05A19] text-[#F05A19] text-sm font-medium
              py-2 rounded-lg hover:bg-[#FEF0EB] transition-colors duration-150"
          >
            <PiArrowSquareOutLight size={16} />
            View
          </a>

          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 flex items-center justify-center gap-1.5
              bg-[#F05A19] text-white text-sm font-medium
              py-2 rounded-lg hover:bg-[#d94e14] transition-colors duration-150
              disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <PiDownloadSimpleLight size={16} />
            {downloading ? "Downloading…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Certificates() {
  const [certs,   setCerts]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    client
      .get("/certificates")
      .then((res) => setCerts(res.data?.data || []))
      .catch(() => toast.error("Could not load certificates."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="py-6 px-4 md:px-8 max-w-5xl mx-auto">

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">My Certificates</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Certificates you have earned from completed courses
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#F05A19]
            rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && certs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center
            justify-center mb-4">
            <PiSealCheckLight size={32} className="text-gray-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-700 mb-1">
            No certificates yet
          </h2>
          <p className="text-sm text-gray-400 max-w-xs">
            Complete a course and get approved to earn your certificate.
          </p>
        </div>
      )}

      {/* Grid */}
      {!loading && certs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {certs.map((cert) => (
            <CertificateCard key={cert.uuid} cert={cert} />
          ))}
        </div>
      )}
    </div>
  );
}
